/**
 * PublicTenderFollows Service
 * Module 4.1 - Public Tender Request
 */

import { BaseService } from '@/chassis/data/services/base-service';
import { BaseFormService, ValidationRules } from '@/chassis/data/services/base-form-service';
import { createSimpleNormalizer } from '@/chassis/data/services/normalizer-utils';
import { FOLLOW_STATUS_OPTIONS, CONTENT_TYPES_OPTIONS } from '@/domains/pages/3.1-registration-module-citizen/constants';

export interface PublicTenderFollowRecord {
  id: string;
  publicTenderId?: string;
  citizenId?: string;
  supplierId?: string;
  followedAt?: string;
  followStatus?: 'active' | 'paused' | 'blocked' | 'muted';
  notificationsEnabled?: boolean;
  contentTypes?: string;
  geographicPriority?: boolean;
  frequencySettings?: string;
  engagementScore?: number | null;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  createdByName?: string;
  createdByUser?: string;
  updatedBy?: string;
  updatedByName?: string;
  updatedByUser?: string;
}

export interface PublicTenderFollowFilters {
  searchTerm?: string;
  followStatus?: string;
  notificationsEnabled?: string;
  active?: string;
  publicTenderId?: string;
  // Escopo por perfil: um follow pertence a UM fornecedor OU a UM cidadão.
  // O filtro garante que cada perfil só veja as próprias licitações monitoradas.
  supplierId?: string;
  citizenId?: string;
  page?: number;
  limit?: number;
}

export interface CreatePublicTenderFollowDto {
  publicTenderId: number | string;
  citizenId?: number | string;
  supplierId?: number | string;
  followedAt: string;
  followStatus: 'active' | 'paused' | 'blocked' | 'muted';
  notificationsEnabled: boolean;
  contentTypes?: string;
  geographicPriority: boolean;
  frequencySettings?: string;
  engagementScore?: number | string | null;
  active?: boolean;
}

export type UpdatePublicTenderFollowDto = Partial<CreatePublicTenderFollowDto>;

export type PublicTenderFollowFormData = CreatePublicTenderFollowDto & {
  publicTenderId: number | string | '';
  citizenId?: number | string | '';
  supplierId?: number | string | '';
  engagementScore?: number | string | null | '';
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  createdByName?: string;
  createdByUser?: string;
  updatedBy?: string;
  updatedByName?: string;
  updatedByUser?: string;
  id?: string;
  recordId?: string;
};

export type PublicTenderFollowFormState = PublicTenderFollowFormData;

const resolveActiveFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return true;
};

const baseNormalize = createSimpleNormalizer<PublicTenderFollowRecord>();

const normalizePublicTenderFollowRecord = (record: PublicTenderFollowRecord): PublicTenderFollowRecord => {
  const normalized = baseNormalize(record);
  const active = resolveActiveFlag(normalized.active ?? normalized.active);
  return {
    ...normalized,
    active,
  };
};

class PublicTenderFollowsService extends BaseService<
  PublicTenderFollowRecord,
  CreatePublicTenderFollowDto,
  UpdatePublicTenderFollowDto,
  PublicTenderFollowFilters
> {
  constructor() {
    super({
      basePath: '/publictenderfollows',
      normalizeRecord: normalizePublicTenderFollowRecord,
    });
  }
}

export const publicTenderFollowsService = new PublicTenderFollowsService();

/**
 * Auto-follow: cria follow para licitação se não existir.
 * Chamado automaticamente ao enviar proposta, dúvida ou impugnação.
 * Fire-and-forget — falha não bloqueia a ação principal.
 */
export async function autoFollowTender(tenderId: string, supplierId?: string): Promise<void> {
    try {
        const existing = await publicTenderFollowsService.list({
            publicTenderId: tenderId,
            ...(supplierId ? { supplierId } : {}),
            limit: 1,
        } as any);
        if ((existing?.items ?? []).length > 0) return; // já segue

        await publicTenderFollowsService.create({
            publicTenderId: tenderId,
            supplierId,
            followedAt: new Date().toISOString(),
            followStatus: 'ACTIVE',
            notificationsEnabled: true,
            active: true,
        } as any);
    } catch {
        // Fire-and-forget — não bloqueia ação principal
    }
}

const toNumberOrUndefined = (value?: number | string | null): number | undefined => {
  if (value === '' || value === undefined || value === null) return undefined;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? undefined : numeric;
};

const toNullableNumber = (value?: number | string | null): number | null | undefined => {
  if (value === '' || value === undefined) return undefined;
  if (value === null) return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const toIsoDateTime = (value?: string): string => {
  if (!value) return new Date().toISOString();
  if (value.includes('Z')) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
};

const toTrimmedString = (value?: string | null) => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const normalizeBoolean = (value: unknown, defaultValue: boolean) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return defaultValue;
};

const sanitizePublicTenderFollowPayload = (
  formData: Partial<PublicTenderFollowFormData>,
): Partial<CreatePublicTenderFollowDto> => {
  const {
    createdAt,
    updatedAt,
    createdBy,
    createdByName,
    createdByUser,
    updatedBy,
    updatedByName,
    updatedByUser,
    ...submittable
  } = formData;

  const payload: Partial<CreatePublicTenderFollowDto> = {
    publicTenderId: submittable.publicTenderId === '' ? undefined : submittable.publicTenderId,
    citizenId: submittable.citizenId === '' ? undefined : submittable.citizenId,
    supplierId: submittable.supplierId === '' ? undefined : submittable.supplierId,
  };

  payload.followedAt = toIsoDateTime(submittable.followedAt as string | undefined);

  const followStatus = toTrimmedString(submittable.followStatus as string | undefined)?.toLowerCase();
  payload.followStatus = (followStatus as CreatePublicTenderFollowDto['followStatus']) || 'active';

  payload.notificationsEnabled = normalizeBoolean(submittable.notificationsEnabled, true);
  payload.geographicPriority = normalizeBoolean(submittable.geographicPriority, false);

  const contentTypes = toTrimmedString(submittable.contentTypes as string | undefined);
  if (contentTypes) payload.contentTypes = contentTypes;

  const frequencySettings = toTrimmedString(submittable.frequencySettings as string | undefined);
  if (frequencySettings) payload.frequencySettings = frequencySettings;

  const engagementScore = submittable.engagementScore;
  if (engagementScore !== '' && engagementScore !== undefined) {
    payload.engagementScore = engagementScore;
  }

  const active = normalizeBoolean(submittable.active ?? submittable.active, true);
  payload.active = active;

  // Ajuste para numeric IDs quando possível
  const parsedTenderId = toNumberOrUndefined(submittable.publicTenderId as any);
  if (parsedTenderId !== undefined) payload.publicTenderId = parsedTenderId;

  const parsedCitizenId = toNumberOrUndefined(submittable.citizenId as any);
  if (parsedCitizenId !== undefined) payload.citizenId = parsedCitizenId;

  const parsedSupplierId = toNumberOrUndefined(submittable.supplierId as any);
  if (parsedSupplierId !== undefined) payload.supplierId = parsedSupplierId;

  const parsedEngagement = toNullableNumber(submittable.engagementScore as any);
  if (parsedEngagement !== undefined) payload.engagementScore = parsedEngagement;

  return payload;
};

export const publicTenderFollowFormDefaults: PublicTenderFollowFormData = {
  publicTenderId: '',
  citizenId: '',
  supplierId: '',
  followedAt: new Date().toISOString(),
  followStatus: 'active',
  notificationsEnabled: true,
  contentTypes: CONTENT_TYPES_OPTIONS?.[0]?.[0] || '',
  geographicPriority: false,
  frequencySettings: '',
  engagementScore: '',
  active: true,
  createdAt: undefined,
  updatedAt: undefined,
  createdBy: undefined,
  createdByName: undefined,
  createdByUser: undefined,
  updatedBy: undefined,
  updatedByName: undefined,
  updatedByUser: undefined,
};

export const createPublicTenderFollowsFormService = () =>
  new BaseFormService<
    PublicTenderFollowRecord,
    PublicTenderFollowFormData,
    CreatePublicTenderFollowDto,
    UpdatePublicTenderFollowDto,
    PublicTenderFollowFilters
  >({
    basePath: '/publictenderfollows',
    initialData: publicTenderFollowFormDefaults,
    normalizeRecord: normalizePublicTenderFollowRecord,
    validationRules: {
      publicTenderId: [ValidationRules.required('ID da licitação é obrigatório')],
      followedAt: [ValidationRules.required('Data de acompanhamento é obrigatória')],
      followStatus: [ValidationRules.required('Status é obrigatório')],
      supplierId: [
        ValidationRules.custom((_value, data) => Boolean((data as any)?.supplierId || (data as any)?.citizenId), 'Informe cidadão ou fornecedor'),
      ],
    },
    transformForCreate: (formData) => sanitizePublicTenderFollowPayload(formData) as CreatePublicTenderFollowDto,
    transformForUpdate: (formData: Partial<PublicTenderFollowFormData>) => sanitizePublicTenderFollowPayload(formData) as UpdatePublicTenderFollowDto,
    transformToForm: (record: PublicTenderFollowRecord) => ({
      ...publicTenderFollowFormDefaults,
      publicTenderId: record.publicTenderId ?? '',
      citizenId: record.citizenId ?? '',
      supplierId: record.supplierId ?? '',
      followedAt: record.followedAt ?? new Date().toISOString(),
      followStatus: record.followStatus ?? 'active',
      notificationsEnabled: record.notificationsEnabled ?? true,
      contentTypes: record.contentTypes ?? '',
      geographicPriority: record.geographicPriority ?? false,
      frequencySettings: record.frequencySettings ?? '',
      engagementScore: record.engagementScore ?? '',
      active: record.active ?? true,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
      createdBy: record.createdBy,
      createdByName: record.createdByName,
      createdByUser: record.createdByUser,
      updatedBy: record.updatedBy,
      updatedByName: record.updatedByName,
      updatedByUser: record.updatedByUser,
    }),
  });
